import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div data-testid="payment-success-page" className="bg-navy min-h-screen pt-16 flex items-center blueprint-grid grain">
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        {state === "polling" && (
          <>
            <Loader2 size={52} className="text-safety mx-auto animate-spin" />
            <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-8">Confirming your payment…</h1>
            <p className="text-slate-400 mt-4">Hold tight — this takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={52} className="text-emerald-400 mx-auto" />
            <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-8">You're enrolled.</h1>
            <p className="text-slate-300 mt-4 leading-relaxed">
              Payment confirmed. WorkBase Hub is unlocked — start with the orientation.
            </p>
            <Link to="/hub" data-testid="payment-enter-hub-btn"
              className="inline-block bg-safety hover:bg-safety-hover text-white font-semibold px-10 py-4 mt-8 transition-colors">
              Enter WorkBase Hub
            </Link>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <Loader2 size={52} className="text-safety mx-auto animate-spin" />
            <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-8">Payment processing.</h1>
            <p className="text-slate-400 mt-4 leading-relaxed">
              Your bank is taking a moment. Once it clears, your Hub unlocks automatically —
              sign back in shortly. Questions? Use the contact page.
            </p>
            <Link to="/hub" className="inline-block border border-white/20 text-white font-semibold px-10 py-4 mt-8 hover:border-safety transition-colors">
              Go to my account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="bg-navy min-h-screen pt-16 flex items-center blueprint-grid grain">
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        <XCircle size={52} className="text-slate-500 mx-auto" />
        <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-8">Checkout cancelled.</h1>
        <p className="text-slate-400 mt-4 leading-relaxed">
          No charge was made. Your acceptance is still on file — complete payment whenever
          you're ready from your account.
        </p>
        <Link to="/hub" data-testid="cancel-return-btn"
          className="inline-block bg-safety hover:bg-safety-hover text-white font-semibold px-10 py-4 mt-8 transition-colors">
          Return to my account
        </Link>
      </div>
    </div>
  );
}
